import { ReactNode, useState, useEffect, useRef } from "react"
import { useRouterState } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { I18nProvider } from "@lingui/react"
import { i18n } from "@lingui/core"
import { useAppStore } from "@src/state"
import { posthog } from "posthog-js"

import { getCommunityLiteralsQuery } from "@src/api/literals"
import { getCommunityApplicationDefinitionPreviewQuery } from "@src/api/application"
import {
  getCurrentUserQuery,
  getCurrentUserCommunitiesQuery,
} from "@src/api/user"
import { useCommunityId } from "@src/hooks/useCommunityId"
import {
  LANGUAGE_ALIASES,
  resolveLanguage,
  toHtmlLang,
} from "@src/utils/language"
import { formatLanguages, compileLiteralOverrides } from "./utils"
import { LoadingIndicator } from "@src/components/atoms/LoadingIndicator"

const SUPPORTED_LOCALES = new Set([
  "en",
  "de",
  "es",
  "fr",
  "it",
  "ko",
  "pt",
  "hr",
  "ru",
  "ro",
  "bg",
  "gr",
  "sr",
  "tr",
  "ar",
  "zh",
])

const getSupportedLocale = (language: string) => {
  const mapped = LANGUAGE_ALIASES[language] ?? language
  return SUPPORTED_LOCALES.has(mapped) ? mapped : "en"
}

// Eagerly start loading English — cached by the bundler, so the fallback is instant
const englishPreload = import("@src/locales/en/messages.ts")

const loadMessages = async (language: string) => {
  const locale = getSupportedLocale(language)
  try {
    const { messages } = await import(`@src/locales/${locale}/messages.ts`)
    return { language: locale, messages }
  } catch (error) {
    posthog.capture("language_load_failed", {
      locale,
      error: error instanceof Error ? error.message : String(error),
    })
    const { messages } = await englishPreload
    return { language: "en", messages }
  }
}

const getBrowserLanguage = () => {
  return navigator.language?.slice(0, 2) ?? "en"
}

const selectAppDefContext = (
  data:
    | { language?: string | null; communityId?: number | null }
    | undefined,
) => ({
  language: data?.language ?? undefined,
  communityId: data?.communityId ?? undefined,
})

const selectApplicationDefinitionId = (state: {
  matches: Array<{ params: Record<string, unknown> }>
}) => {
  for (const match of state.matches) {
    const raw = match.params.applicationDefinitionId
    if (typeof raw === "string") {
      const id = parseInt(raw, 10)
      return isNaN(id) ? undefined : id
    }
  }
  return undefined
}

export const LanguageApplicationWrapper = ({
  children,
}: {
  children: ReactNode
}) => {
  const [languageActivated, setLanguageActivated] = useState(false)
  const routeCommunityId = useCommunityId()
  const token = useAppStore.getState().token
  const adminToken = useAppStore((state) => state.adminToken)
  const mustAcceptTos = useAppStore((state) => state.mustAcceptTos)
  const isAuthenticated = !!token

  const applicationDefinitionId = useRouterState({
    select: selectApplicationDefinitionId,
  })

  // Fetch the application definition (cached) for its language and communityId.
  // The communityId lets logged-in applicants see the community's literals on the
  // apply route, which has no communityId in the URL.
  const { data: appDefContext, isFetched: isAppDefFetched } = useQuery({
    ...getCommunityApplicationDefinitionPreviewQuery(applicationDefinitionId),
    enabled: !!applicationDefinitionId,
    select: selectAppDefContext,
  })
  const appDefLanguage = appDefContext?.language
  const appDefCommunityId = appDefContext?.communityId

  const { data: currentUser, isError: isUserError } = useQuery({
    ...getCurrentUserQuery(),
    enabled: isAuthenticated,
  })

  const { data: userCommunities } = useQuery({
    ...getCurrentUserCommunitiesQuery(),
    enabled: isAuthenticated,
  })

  // Whitelabel fallback: when the route has no communityId (e.g. /account, /landing)
  // AND the user belongs to exactly one community, apply that community's literals
  // app-wide. Multi-community users (incl. super admins) keep the defaults outside
  // community routes, which avoids polluting /super-admin and route transitions.
  const singleCommunityFallbackId =
    userCommunities?.length === 1
      ? (currentUser?.primaryCommunityId ?? undefined)
      : undefined
  const literalsCommunityId =
    routeCommunityId ?? appDefCommunityId ?? singleCommunityFallbackId

  // Only fetch when authenticated — unauthenticated users use browser language
  const { data: communityLiterals } = useQuery({
    ...getCommunityLiteralsQuery(literalsCommunityId),
    enabled: isAuthenticated && !!literalsCommunityId,
  })

  const canActivateLanguage = isAuthenticated
    ? mustAcceptTos || isUserError || !!currentUser
    : !applicationDefinitionId || isAppDefFetched

  useEffect(() => {
    if (!canActivateLanguage) return

    const activate = async () => {
      try {
        const requestedLanguage = resolveLanguage(
          currentUser?.language,
          appDefLanguage,
          getBrowserLanguage(),
        )

        const { language, messages } = await loadMessages(requestedLanguage)

        const replacements = {
          "100": communityLiterals?.primitive_startup ?? "Startup",
          "101": communityLiterals?.primitive_startups ?? "Startups",
          "200": communityLiterals?.primitive_program ?? "Program",
          "201": communityLiterals?.primitive_programs ?? "Programs",
        }

        const mergedMessages = {
          ...messages,
          ...compileLiteralOverrides(communityLiterals),
        }

        const processedMessages = formatLanguages(mergedMessages, replacements)
        i18n.load(language, processedMessages)
        i18n.activate(language)
        // Chrome offers to translate whenever rendered content disagrees with lang
        document.documentElement.lang = toHtmlLang(language)
      } catch (error) {
        posthog.capture("language_activation_failed", {
          error: error instanceof Error ? error.message : String(error),
        })
      }
      setLanguageActivated(true)
    }

    activate()
  }, [canActivateLanguage, currentUser, communityLiterals, appDefLanguage])

  const debugActiveRef = useRef(false)
  const savedLocaleRef = useRef<{ language: string; messages: Record<string, string> } | null>(null)

  useEffect(() => {
    if (!currentUser?.isPlatformAdmin && !adminToken) return

    const isMac = navigator.platform.startsWith("Mac")

    const isTriggered = (e: KeyboardEvent) =>
      isMac
        ? e.metaKey && e.ctrlKey && e.altKey
        : e.ctrlKey && e.shiftKey && e.altKey

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isTriggered(e) && !debugActiveRef.current) {
        debugActiveRef.current = true
        savedLocaleRef.current = {
          language: i18n.locale,
          messages: i18n.messages as Record<string, string>,
        }
        const passthrough = Object.fromEntries(
          Object.keys(i18n.messages).map((k) => [k, k]),
        )
        i18n.load("__debug__", passthrough)
        i18n.activate("__debug__")
      }
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      if (!isTriggered(e) && debugActiveRef.current) {
        debugActiveRef.current = false
        if (savedLocaleRef.current) {
          const { language, messages } = savedLocaleRef.current
          i18n.load(language, messages)
          i18n.activate(language)
          savedLocaleRef.current = null
        }
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    window.addEventListener("keyup", handleKeyUp)
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
      window.removeEventListener("keyup", handleKeyUp)
    }
  }, [currentUser?.isPlatformAdmin, adminToken])

  if (!languageActivated && isAuthenticated) {
    return <LoadingIndicator />
  }

  return <I18nProvider i18n={i18n}>{children}</I18nProvider>
}
